// 法人情報新規登録
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import { Button } from 'react-bootstrap';
import axios from '../../../axios';
import { api_response_type } from '../../../Const';
import { handleBrowserBack } from '../../../BrowserBack';
import { isBrowserIe } from '../../../BrowserJudgment';
import {
  InputDataType,
  InputDataArray,
  GroupLabelType,
  ResponseDetailType,
  ValidMessageType,
  CorporateRequestType,
} from '../../../types/Corporate';
import { InicialinputData, GroupLabel } from '../../../const/Corporate';
import LoadingModal from '../../../molecules/LoadingModal';
import CorporateEntryInputGroup from '../../../organisms/CorporateEntryInputGroup';
import CorporateInfoConfirm from '../../../organisms/CorporateInfoConfirm';
import CorporateCreateEditComment from '../../../organisms/CorporateCreateEditComment';
import { KWMNTGM05020A02, KWMNTGM05050A02 } from '../../../const/api_modules';

const CorporateEntry = () => {
  const [input_data, setInputData] = useState<InputDataArray>(InicialinputData);
  const [verified_data, setVerifiedData] = useState<ResponseDetailType | undefined>(undefined);
  const [edit_comment, setEditComment] = useState('');
  const [is_loading, setIsLoading] = useState(false);
  // 確認画面を表示するフラグ
  const [is_confirm, setIsConfirm] = useState(false);
  // エラーメッセージ
  const [valid_message, setValidMessage] = useState<ValidMessageType[]>([]);

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
  }, []);

  /**
   * フィールドに入力された値をstateにセットする処理
   * @param event inputフィールドのevent
   * @returns stateに値をセット
   */
  const handleInput = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, name, value } = event.target;
    const array = input_data[id];
    // 配列から入力されたアイテムのnameを探し、そのindexにvalueをセットする
    const index = array.findIndex((item: InputDataType) => item.name === name);
    array[index].value = value;
    setInputData({ ...input_data, [id]: array });
  };

  /**
   * フォーカスアウト時のイベントハンドラ
   * @param event inputフィールドのevent
   */
  const handleBlur = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, name, value } = event.target;
    const array = input_data[id];
    const index = array.findIndex((item: InputDataType) => item.name === name);
    // 法人名(漢字),法人名(フリガナ)に全角スペースが混入している場合は半角スペースに修正する
    if (name === input_data.corporate[0].name || name === input_data.corporate[1].name) {
      array[index].value = value.replace('　', ' ');
      setInputData(prev => {
        return { ...prev, [id]: array };
      });
    }
  };

  /**
   * 【登録】ボタン押下時の処理
   */
  const handleEntry = async () => {
    try {
      // ローディング画面表示
      setIsLoading(true);
      // バリデーション初期化
      setValidMessage([]);

      const detail = [
        { id: 0, name: 'temp_corporate_name', value: input_data.corporate[0].value },
        { id: 0, name: 'temp_corporate_kana_name', value: input_data.corporate[1].value },
        { id: 0, name: 'temp_address', value: input_data.corporate[2].value },
        { id: 0, name: 'temp_email_1', value: input_data.user1[0].value },
        { id: 0, name: 'temp_position_name_1', value: input_data.user1[1].value },
        { id: 0, name: 'temp_name_1', value: input_data.user1[2].value },
        { id: 0, name: 'temp_email_2', value: input_data.user2[0].value },
        { id: 0, name: 'temp_position_name_2', value: input_data.user2[1].value },
        { id: 0, name: 'temp_name_2', value: input_data.user2[2].value },
        { id: 0, name: 'temp_email_3', value: input_data.user3[0].value },
        { id: 0, name: 'temp_position_name_3', value: input_data.user3[1].value },
        { id: 0, name: 'temp_name_3', value: input_data.user3[2].value },
        { id: 0, name: 'temp_comment', value: edit_comment },
      ];

      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
        module_name : KWMNTGM05020A02,
        is_json : true,
        reception_id : '0',
        reception_cd : null,
        detail : detail,
      });

      // APIからの応答種別を判定
      if (response.data.response_type === api_response_type.validation) {
        // バリデーションエラーをセット
        setValidMessage(response.data.detail);
      } else if (
        response.data.response_type === api_response_type.transition_to_corporate_entry_confirm
      ) {
        // チェック済のデータをセットして確認画面を表示
        setVerifiedData(response.data.detail);
        setIsConfirm(true);
      }
    } catch (error) {
      console.error(error.response.data.message);
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 【確認】ボタン押下時の処理
   */
  const handleConfirm = async () => {
    try {
      // ローディング画面表示
      setIsLoading(true);

      let detail: CorporateRequestType[] = [];
      const info = verified_data?.corporate_information;
      if (info) {
        detail = [
          { id: 0, name: 'temp_corporate_name', value: info.temp_corporate_name },
          { id: 0, name: 'temp_corporate_kana_name', value: info.temp_corporate_kana_name },
          { id: 0, name: 'temp_address', value: info.temp_address },
          { id: 0, name: 'temp_email_1', value: info.temp_email_1 },
          { id: 0, name: 'temp_position_name_1', value: info.temp_position_name_1 },
          { id: 0, name: 'temp_name_1', value: info.temp_name_1 },
          { id: 0, name: 'temp_email_2', value: info.temp_email_2 },
          { id: 0, name: 'temp_position_name_2', value: info.temp_position_name_2 },
          { id: 0, name: 'temp_name_2', value: info.temp_name_2 },
          { id: 0, name: 'temp_email_3', value: info.temp_email_3 },
          { id: 0, name: 'temp_position_name_3', value: info.temp_position_name_3 },
          { id: 0, name: 'temp_name_3', value: info.temp_name_3 },
          { id: 0, name: 'temp_comment', value: info.temp_comment },
        ];
      }

      const response = await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
        module_name : KWMNTGM05050A02,
        is_json : true,
        reception_id : '0',
        reception_cd : null,
        detail : detail,
      });

      // APIからの応答種別を判定
      if (response.data.response_type === api_response_type.navigation_finish) {
        // 完了画面に遷移
        location.replace(
          `${location.origin}${process.env.MIX_MOCK_URL}/staff/corporate_info_edit_complete`
        );
      }
    } catch (error) {
      console.error(error.response.data.message);
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 【キャンセル】ボタン押下時の処理
   */
  const backTransition = () => {
    if (isBrowserIe()) {
      const node = document.getElementById('corporate-entry');
      location.replace(node?.dataset.referer as string);
    } else {
      location.replace(document.referrer);
    }
  };

  return (
    <div className="corporate">
      <p className="title">法人情報</p>
      <div className="container">
        <div className="contents">
          {!is_confirm ? (
            <>
              <p className="contents_title">法人情報新規登録</p>
              <div className="input_field_wrapper">
                {GroupLabel.map((item: GroupLabelType) => {
                  return (
                    <React.Fragment key={item.type}>
                      <p className="section_label">{item.label}</p>
                      <CorporateEntryInputGroup
                        id={item.type}
                        input_data={input_data[item.type]}
                        handleInput={handleInput}
                        handleBlur={handleBlur}
                        valid_message={valid_message}
                      />
                    </React.Fragment>
                  );
                })}
                <CorporateCreateEditComment
                  margin="mt-5"
                  edit_comment={edit_comment}
                  setEditComment={setEditComment}
                  valid_message={valid_message}
                />
              </div>
              <div className="w_button_section mx-auto my-5 d-flex justify-content-between">
                <Button
                  className="btn-mini btn-text-color"
                  variant="outline-success"
                  onClick={backTransition}
                >
                  キャンセル
                </Button>
                <Button className="btn-mini btn-success" onClick={handleEntry}>
                  登録
                </Button>
              </div>
            </>
          ) : (
            <CorporateInfoConfirm
              verified_data={verified_data}
              setIsConfirm={setIsConfirm}
              handleConfirm={handleConfirm}
            />
          )}
        </div>
      </div>
      <LoadingModal show={is_loading} />
    </div>
  );
};

if (document.getElementById('corporate-entry')) {
  render(<CorporateEntry />, document.getElementById('corporate-entry'));
}
